import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Columns3 } from 'lucide-react';
import { useListPrefs } from '../../lib/prefs';
import { cn } from '../../components/ui';

/** A column of a report table. `key` is the API's column key, so the same selection drives screen, print and export. */
export interface ReportColumn<T> {
  key: string;
  label: string;
  /** Identifier columns cannot be hidden (the export keeps them too). */
  required?: boolean;
  numeric?: boolean;
  render: (row: T) => ReactNode;
}

/**
 * Column visibility of one report table, remembered server-side per user (`user_preferences`), not in the
 * browser: the reader's layout follows them to another device, and the export receives exactly this selection.
 */
export function useReportColumns<T>(section: string, columns: Array<ReportColumn<T>>) {
  const { listPrefs, setListPrefs } = useListPrefs(`report.strategy_consolidated.${section}`);
  const all = columns.map((c) => c.key);
  // The preference stores the HIDDEN keys (prefixed `-`), so a column that appears later (a new impact unit)
  // starts visible instead of being mistaken for one the reader switched off.
  const hidden = new Set((listPrefs.columns ?? []).filter((k) => k.startsWith('-')).map((k) => k.slice(1)));
  const visible = new Set(all.filter((k) => !hidden.has(k)));
  for (const c of columns) if (c.required) visible.add(c.key);
  const toggle = (key: string) => {
    const next = new Set(hidden);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setListPrefs({ columns: [...next].map((k) => `-${k}`) });
  };
  const customised = all.some((k) => !visible.has(k));
  return { visible, toggle, customised };
}

export function ReportTable<T>({ columns, rows, visible, onToggle, rowKey, empty }: { columns: Array<ReportColumn<T>>; rows: T[]; visible: Set<string>; onToggle: (key: string) => void; rowKey: (row: T, index: number) => string; empty: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const shown = columns.filter((c) => visible.has(c.key));
  return (
    <div>
      <div className="no-print relative mb-1 flex justify-end">
        <button type="button" className="btn-secondary px-2 py-1 text-xs" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          <Columns3 size={14} /> {t('reports.columns')}
        </button>
        {open && (
          <div className="absolute end-0 top-8 z-20 max-h-72 w-60 overflow-auto rounded-lg border border-neutral-200 bg-white p-2 text-sm shadow-lg dark:border-neutral-700 dark:bg-neutral-900">
            {columns.map((c) => (
              <label key={c.key} className={cn('flex items-center gap-2 rounded px-1 py-0.5', c.required ? 'opacity-60' : 'cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800')}>
                <input type="checkbox" checked={visible.has(c.key)} disabled={c.required} onChange={() => onToggle(c.key)} />
                <span dir="auto">{c.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>
      {rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-neutral-200 p-4 text-center text-sm text-neutral-500 dark:border-neutral-700">{empty}</p>
      ) : (
        <div className="report-table-wrap overflow-x-auto">
          <table className="report-table w-full border-collapse text-sm">
            <thead>
              <tr>
                {shown.map((c) => (
                  <th key={c.key} className={cn('border-b border-neutral-300 px-2 py-1.5 text-start text-xs font-semibold text-neutral-600 dark:border-neutral-600 dark:text-neutral-300', c.numeric && 'text-end')}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={rowKey(r, i)} className="border-b border-neutral-100 align-top dark:border-neutral-800">
                  {shown.map((c) => (
                    <td key={c.key} className={cn('px-2 py-1.5', c.numeric && 'text-end tabular-nums')}>
                      {c.render(r)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function ReportSection({ id, title, subtitle, children }: { id: string; title: string; subtitle?: string; children: ReactNode }) {
  return (
    <section id={`report-${id}`} className="report-section card mb-4 p-4">
      <h2 className="text-base font-semibold">{title}</h2>
      {subtitle && <p className="mb-2 text-xs text-neutral-500">{subtitle}</p>}
      <div className="mt-2">{children}</div>
    </section>
  );
}

export function Figure({ value, label, sub, tone = 'ok' }: { value: string; label: string; sub?: string; tone?: 'ok' | 'warn' | 'danger' }) {
  return (
    <div className="report-figure rounded-lg border border-neutral-200 p-3 dark:border-neutral-700">
      <div className={cn('text-xl font-semibold tabular-nums', tone === 'warn' && 'text-amber-600', tone === 'danger' && 'text-red-600')}>{value}</div>
      <div className="text-xs text-neutral-600 dark:text-neutral-300">{label}</div>
      {sub && <div className="mt-0.5 text-[11px] text-neutral-500" dir="auto">{sub}</div>}
    </div>
  );
}
